import React, { useCallback, useEffect, useRef } from 'react'
import { useInView } from 'react-intersection-observer'
import { useReducedMotion } from '../hooks/useReducedMotion'
import { useIsHydrated } from '../hooks/useIsHydrated'
/* network.gif (2.3 MB) replaced by an H.264 loop (0.5 MB) + webp poster.
 * The <video> mounts with preload="none" and only starts playing when the
 * section scrolls into view, so it stays off the critical path like the
 * old loading="lazy" gif did. */
import NetworkVideo from '../../assets/network.mp4'
import NetworkPoster from '../../assets/network-poster.webp'

export default function TrustedInTheProcess(props) {
    /* Two observers on one node: `inView` latches once, 200px early, and is
     * what MOUNTS the video; `onScreen` is live and is what plays it. */
    const { ref: mountRef, inView } = useInView({ triggerOnce: true, rootMargin: '200px 0px' })
    const { ref: screenRef, inView: onScreen } = useInView({ threshold: 0.15 })
    const videoContainerRef = useCallback(
        (node) => {
            mountRef(node)
            screenRef(node)
        },
        [mountRef, screenRef]
    )
    const reducedMotion = useReducedMotion()
    const hydrated = useIsHydrated()
    const videoRef = useRef(null)

    /* The loop runs while you are looking at it and stops when you are not.
     * That is the stop mechanism moving content owes the viewer (WCAG 2.2.2),
     * and it keeps a decorative loop off the CPU for the rest of the page.
     * Someone who asked for less motion gets the poster and no video at all. */
    useEffect(() => {
        const el = videoRef.current
        if (!el) return
        if (onScreen) {
            const started = el.play()
            if (started && started.catch) started.catch(() => {})
        } else {
            el.pause()
        }
    }, [onScreen])

    /* <video> and the poster <img> are different element trees, so the swap
     * waits for hydration — the prerender captures post-effect DOM and would
     * otherwise bake in a <video> that the first client render does not draw. */
    const showVideo = hydrated && inView && !reducedMotion

    return (
        <section id='trust' ref={props.sectionRef} className="relative min-h-[100svh] bg-black section5 w-full flex flex-col items-center">
            <div data-reveal className='mt-[52px] c-md:mt-[56px] c-lg:mt-[90px] c-xl:mt-[125px] flex flex-col text-center items-center justify-center z-20 w-full nav-sm:w-[60%] c-xl:w-[37%] px-[36px] nav-sm:px-0'>
                <h2 className='text-36 c-xl:text-60 font-bold text-white'>TRUST IN THE PROCESS</h2>
                <p className='text-18 c-md:text-24 c-xl:text-30 text-white mt-[33px]'>Sometimes it looks messy, however our <span className='font-bold'>experts</span> hold the leash tight!</p>
            </div>

            <div className='w-full flex flex-1 flex-col items-center justify-center mt-[-22px]'>
                <div ref={videoContainerRef} className='relative flex-grow flex flex-col items-center justify-center gif-width'>
                    {showVideo ? (
                        <video
                            ref={videoRef}
                            autoPlay
                            muted
                            loop
                            playsInline
                            preload='none'
                            poster={NetworkPoster}
                            width={500}
                            height={500}
                            aria-hidden='true'
                            className='w-full h-auto'>
                                <source src={NetworkVideo} type='video/mp4' />
                        </video>
                    ) : (
                        <img src={NetworkPoster} alt='' width={500} height={500} className='w-full h-auto' loading='lazy' decoding='async' />
                    )}
                </div>
            </div>
        </section>
    )
}
